import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import supabase from '../../../supabase/supabase';
import SafeIcon from '../../../common/SafeIcon';
import { 
  FiSearch, 
  FiMoreVertical, 
  FiEdit2, 
  FiTrash2, 
  FiSlash, 
  FiExternalLink, 
  FiShield, 
  FiStar,
  FiUser
} from 'react-icons/fi';

const ActionDropdown = ({ user, onEdit, onSuspend, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, right: 0 });

  const toggleDropdown = (e) => {
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 8, // 8px spacing below the button
        right: document.documentElement.clientWidth - rect.right // Align to the right edge of the button
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target) &&
        buttonRef.current && !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      // Close dropdown when scrolling to prevent it from detaching from the row
      if (isOpen) setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Use capture phase (true) to catch scroll events on any internal scrollable containers
      window.addEventListener('scroll', handleScroll, true); 
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  return (
    <>
      <button 
        ref={buttonRef}
        onClick={toggleDropdown}
        className="p-2 rounded-xl text-zinc-400 hover:text-amber-100 hover:bg-white/5 transition-colors focus:outline-none"
      >
        <SafeIcon icon={FiMoreVertical} />
      </button>

      {createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div 
              ref={dropdownRef}
              initial={{ opacity: 0, scale: 0.95, y: -10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -10 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                top: `${coords.top}px`,
                right: `${coords.right}px`
              }}
              className="w-40 bg-zinc-900/95 backdrop-blur-xl border border-orange-500/20 rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] z-[9999] overflow-hidden py-1 origin-top-right"
            >
              <button 
                onClick={() => { onEdit(user); setIsOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-zinc-300 hover:bg-orange-500/10 hover:text-amber-100 flex items-center gap-2.5 transition-colors"
              >
                <SafeIcon icon={FiEdit2} className="text-zinc-500" /> Edit User
              </button>
              <button 
                onClick={() => { onSuspend(user); setIsOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-orange-400 hover:bg-orange-500/10 flex items-center gap-2.5 transition-colors"
              >
                <SafeIcon icon={FiSlash} className="text-orange-500" /> Suspend
              </button>
              <div className="h-px w-full bg-white/5 my-1"></div>
              <button 
                onClick={() => { onDelete(user); setIsOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-2.5 transition-colors"
              >
                <SafeIcon icon={FiTrash2} className="text-red-500" /> Delete
              </button>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;
        setUsers(data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleEdit = (user) => {
    alert(`Edit user: ${user.email}\n(Feature coming soon)`);
  };

  const handleSuspend = (user) => {
    alert(`Suspend user: ${user.email}\n(Feature coming soon)`);
  };

  const handleDelete = (user) => {
    if (confirm(`Are you sure you want to delete ${user.email}? This action cannot be undone.`)) {
      alert(`Deleted user: ${user.email}\n(Feature coming soon)`);
    }
  };

  const filteredUsers = users.filter(u => 
    (u.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(search.toLowerCase())
  );

  const totalPro = users.filter(u => u.is_pro).length;

  if (loading) {
    return (
      <div className="py-20 flex justify-center w-full">
        <div className="w-10 h-10 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-7xl mx-auto pb-12"
    >
      {/* Top Stats & Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8 mt-2">
        <div className="flex gap-4">
          <div className="px-5 py-3 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center border border-orange-500/20">
              <SafeIcon icon={FiUser} className="text-orange-400 text-lg" />
            </div>
            <div>
              <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Total Users</div>
              <div className="text-xl font-bold text-amber-50">{users.length}</div>
            </div>
          </div>
          <div className="px-5 py-3 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 shadow-sm flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center border border-yellow-500/20">
              <SafeIcon icon={FiStar} className="text-yellow-400 text-lg" />
            </div>
            <div>
              <div className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Pro Users</div>
              <div className="text-xl font-bold text-amber-50">{totalPro}</div>
            </div>
          </div>
        </div>

        <div className="relative w-full md:w-80">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input 
            type="text" 
            placeholder="Search users by name or email..." 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-zinc-900/50 backdrop-blur-md border border-orange-500/20 rounded-xl pl-11 pr-4 py-3 text-sm text-zinc-200 placeholder-zinc-600 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none shadow-inner"
          />
        </div>
      </div>

      {/* Table Card */}
      <div className="rounded-3xl bg-zinc-900/40 backdrop-blur-xl border border-orange-500/10 shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead className="bg-[#0a0500]/80 border-b border-orange-500/10">
              <tr>
                <th className="py-4 px-6 text-xs font-semibold tracking-wider text-zinc-500 uppercase">User</th>
                <th className="py-4 px-6 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Status</th>
                <th className="py-4 px-6 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Public Page</th>
                <th className="py-4 px-6 text-xs font-semibold tracking-wider text-zinc-500 uppercase">Joined</th>
                <th className="py-4 px-6 text-xs font-semibold tracking-wider text-zinc-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 bg-zinc-950/30">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-orange-500/5 transition-colors group">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full border border-orange-500/30 overflow-hidden bg-gradient-to-br from-orange-500/20 to-yellow-500/20 flex items-center justify-center shadow-sm flex-shrink-0">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-amber-100 font-semibold text-sm">
                              {user.full_name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          )}
                        </div>
                        <div className="overflow-hidden">
                          <div className="text-amber-50 font-medium truncate flex items-center gap-2">
                            {user.full_name || 'Unnamed User'}
                            {user.role === 'superadmin' && (
                              <SafeIcon icon={FiShield} className="text-orange-500 text-xs" title="Superadmin" />
                            )}
                          </div>
                          <div className="text-xs text-zinc-500 truncate">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {user.is_pro ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r from-yellow-400/10 to-amber-600/10 border border-yellow-500/20 text-yellow-500 text-[10px] font-bold uppercase tracking-wider shadow-[0_0_10px_rgba(234,179,8,0.1)]">
                          <SafeIcon icon={FiStar} /> Pro
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-zinc-800/50 border border-zinc-700/50 text-zinc-400 text-[10px] font-bold uppercase tracking-wider">
                          Free
                        </span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      {user.public_id ? (
                        <a 
                          href={`https://moment.reviews/p/${user.public_id}`} 
                          target="_blank" 
                          rel="noreferrer" 
                          className="inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors"
                        >
                          <SafeIcon icon={FiExternalLink} className="text-xs" />
                          /{user.public_id}
                        </a>
                      ) : (
                        <span className="text-zinc-600 text-sm italic">Not configured</span>
                      )}
                    </td>
                    <td className="py-4 px-6 text-sm text-zinc-400">
                      {new Date(user.created_at).toLocaleDateString(undefined, { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                    <td className="py-4 px-6 text-right">
                      <ActionDropdown 
                        user={user} 
                        onEdit={handleEdit} 
                        onSuspend={handleSuspend} 
                        onDelete={handleDelete} 
                      />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-12 text-center text-zinc-500">
                    No users found matching your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default Users;